import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getToken as getAppCheckToken } from 'firebase/app-check';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { appCheck, db } from '../firebase';
import { uploadAvatar, isCloudinaryEnabled, imageFileToDataUrl } from '../utils/uploads';
import { collection, collectionGroup, getDocs, query, where, writeBatch } from 'firebase/firestore';
import {
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';

export default function Profile() {
  const { user, auth, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [file, setFile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [uploadPct, setUploadPct] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user?.displayName) setDisplayName(user.displayName);
    if (user?.email) setEmail(user.email);
  }, [user]);

  const avatarPreview = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    // Use a locally cached preview if present (no external service)
    try {
      if (typeof window !== 'undefined' && user?.uid) {
        const cached = localStorage.getItem(`LOCAL_AVATAR_${user.uid}`);
        if (cached) return cached;
      }
    } catch (_) {}
    return user?.photoURL || '';
  }, [file, user]);

  if (loading) {
    return <div style={{ paddingTop: '4rem', textAlign: 'center' }}>Loading…</div>;
  }

  if (!user) {
    return (
      <div style={{ paddingTop: '4rem', textAlign: 'center' }}>
        Please sign in to view your profile.
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setSavingProfile(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const isR2WorkerEnabled = !!(process.env.REACT_APP_AVATAR_R2_WORKER_URL || '').trim();

      // If using Cloudinary or R2 Worker, skip App Check entirely; otherwise warm token to avoid preflight failures
      if (!isCloudinaryEnabled() && !isR2WorkerEnabled) {
        let appCheckOk = true;
        try {
          if (appCheck) {
            await getAppCheckToken(appCheck, false);
          }
        } catch (e) {
          appCheckOk = false;
          try { console.warn('App Check token fetch failed (will proceed anyway):', e?.message || e); } catch {}
        }
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost' && !appCheckOk) {
          setSavingProfile(false);
          setErrorMsg('App Check is enforced and your localhost debug token isn\'t registered. Open DevTools, copy the App Check debug token, add it in Firebase Console → App Check → Manage debug tokens, then reload. Or temporarily set Storage App Check to Monitoring while developing.');
          return;
        }
      }
      let remotePhotoURL = null;
      if (file) {
        // Basic size/type guard (optional): 10MB limit
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('Image is too large. Please pick a file under 10MB.');
        }
        try {
          // Use Cloudinary if configured, else Firebase Storage
          const url = await uploadAvatar(file, user, (pct) => setUploadPct(pct));
          const sep = url.includes('?') ? '&' : '?';
          remotePhotoURL = `${url}${sep}v=${Date.now()}`;
          try { if (user?.uid) localStorage.removeItem(`LOCAL_AVATAR_${user.uid}`); } catch (_) {}
        } catch (uploadErr) {
          // As a last resort, embed a small optimized data URL so the app shows an avatar without external services
          try {
            const dataUrl = await imageFileToDataUrl(file, 128, 'image/webp', 0.7);
            // Persist locally for preview; DO NOT set in Firebase Auth (too long for photoURL)
            try { if (user?.uid) localStorage.setItem(`LOCAL_AVATAR_${user.uid}`, dataUrl); } catch (_) {}
            setSuccessMsg('Saved a local avatar preview. Configure Cloudinary or Firebase to persist it across devices.');
          } catch (_) {
            throw uploadErr; // rethrow original upload error if fallback also fails
          }
        }
      }
      const profileUpdates = { displayName: displayName || undefined };
      if (remotePhotoURL) profileUpdates.photoURL = remotePhotoURL;
      await updateProfile(auth.currentUser, profileUpdates);
      // Refresh auth state and clear local file to update preview
      await refreshUser();

      // Propagate updated name/photo to authored forum content so it reflects everywhere
      try {
        const authorName = (displayName || user.email || '');
        const updates = [];
        // Threads by this user
        const threadsQ = query(collection(db, 'threads'), where('authorId', '==', user.uid));
        const threadsSnap = await getDocs(threadsQ);
        threadsSnap.forEach((d) => {
          const data = { authorName };
          if (remotePhotoURL) data.authorPhotoURL = remotePhotoURL;
          updates.push({ ref: d.ref, data });
        });
        // Comments by this user across all threads (collection group query)
        const commentsQ = query(collectionGroup(db, 'comments'), where('authorId', '==', user.uid));
        const commentsSnap = await getDocs(commentsQ);
        commentsSnap.forEach((d) => {
          const data = { authorName };
          if (remotePhotoURL) data.authorPhotoURL = remotePhotoURL;
          updates.push({ ref: d.ref, data });
        });
        // Batch in chunks to avoid 500 write limit
        for (let i = 0; i < updates.length; i += 400) {
          const batch = writeBatch(db);
          updates.slice(i, i + 400).forEach((u) => batch.update(u.ref, u.data));
          await batch.commit();
        }
      } catch (propErr) {
        try { console.warn('Profile propagated to content with warnings:', propErr); } catch {}
        // Non-fatal: UI will still show updated avatar for new content; existing content may keep old photo
      }
      setFile(null);
      setUploadPct(0);
      setSuccessMsg('Profile updated');
    } catch (err) {
      let friendly = err?.message || 'Failed to update profile';
      // Cloudinary-specific guidance: missing or wrong upload preset
      if (/upload preset/i.test(friendly)) {
        friendly = 'Cloudinary upload preset not found. Create an unsigned preset in Cloudinary (Settings → Upload → Upload presets) and set it via .env (REACT_APP_CLOUDINARY_UPLOAD_PRESET) or localStorage (CLD_UPLOAD_PRESET).';
      }
      if (err?.code === 'storage/canceled') {
        friendly = err?.message || 'Upload canceled. Please try again.';
      } else if (err?.code === 'storage/unauthorized') {
        friendly = 'Upload blocked by Firebase Storage rules. Ensure rules allow avatars/{uid}/avatar.ext for the current user.';
      } else if ((err?.code === 'storage/unknown' || err?.code === 'permission-denied') && /xhr|network|preflight|cors|app.?check/i.test(err?.message || '')) {
        friendly = 'Upload failed due to a network/preflight restriction. If you\'re on localhost, register the Firebase App Check debug token in the Console or set App Check enforcement for Storage to Monitoring while testing.';
      } else if (err?.code === 'auth/invalid-profile-attribute') {
        friendly = 'Firebase rejected the photo URL (likely too long). Your local preview is saved; configure Cloudinary or Firebase to persist across devices.';
      }
      setErrorMsg(friendly + (err?.code && !friendly.includes(err.code) ? ` [${err.code}]` : ''));
    } finally {
      setSavingProfile(false);
    }
  };

  const reauth = async () => {
    if (!user?.email) throw new Error('Missing user email for reauthentication');
    if (!currentPassword) throw new Error('Please enter your current password');
    const cred = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, cred);
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!user) return;
    setSavingEmail(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      await reauth();
      await updateEmail(auth.currentUser, email);
      try { await auth.currentUser.reload(); } catch {}
      setSuccessMsg('Email updated');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (newPassword.length < 6) {
      setErrorMsg('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMsg('New passwords do not match');
      return;
    }
    setSavingPassword(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      await reauth();
      await updatePassword(auth.currentUser, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setSuccessMsg('Password updated!');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div style={{ paddingTop: '4rem', display: 'flex', justifyContent: 'center' }}>
  <form onSubmit={handleSubmit} className="glass-card form-glass" style={{ padding: 24 }}>
        <h2 className="card-title" style={{ marginTop: 0 }}>Your Profile</h2>
  {errorMsg && <div className="form-row meta-text" style={{ color: '#ff6b6b', marginBottom: 8 }}>{errorMsg}</div>}
  {successMsg && <div className="form-row meta-text" style={{ color: '#4caf50', marginBottom: 8 }}>{successMsg}</div>}
        <div className="form-row" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 96, height: 96, borderRadius: '50%', overflow: 'hidden', background: '#222' }}>
            {avatarPreview ? (
              <img src={avatarPreview} alt="avatar preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>No photo</div>
            )}
          </div>
          <div className="form-row" style={{ display: 'flex', justifyContent: 'center' }}>
            <button type="button" className="btn-glass-secondary" onClick={() => fileInputRef.current?.click()}>
              Change photo
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
            {file && <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6, textAlign: 'center' }}>{file.name}</div>}
          </div>
          {savingProfile && (
            <div className="form-row" style={{ width: '100%' }}>
              <div className="progress">
                <div className="progress-bar" style={{ width: `${uploadPct > 2 ? uploadPct : (uploadPct > 0 ? 2 : 0)}%` }} />
              </div>
            </div>
          )}
        </div>

        <label style={{ display: 'block', textAlign: 'left', marginBottom: 12 }}>
          <span className="form-label">Display name</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your name"
            className="input-glass"
          />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginTop: 8 }}>
          <fieldset style={{ border: '1px solid #333', borderRadius: 12, padding: 16 }}>
            <legend style={{ padding: '0 6px' }}>Account email</legend>
            <label style={{ display: 'block', textAlign: 'left', marginBottom: 12 }}>
              <span className="form-label">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-glass"
              />
            </label>
            <label style={{ display: 'block', textAlign: 'left', marginBottom: 12 }}>
              <span className="form-label">Current password (for security)</span>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="input-glass"
              />
            </label>
            <button onClick={handleUpdateEmail} disabled={savingEmail} type="button" className="btn-glass-primary btn-block">
              {savingEmail ? 'Updating…' : 'Update email'}
            </button>
          </fieldset>

          <fieldset style={{ border: '1px solid #333', borderRadius: 12, padding: 16 }}>
            <legend style={{ padding: '0 6px' }}>Change password</legend>
            <label style={{ display: 'block', textAlign: 'left', marginBottom: 12 }}>
              <span className="form-label">Current password</span>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="input-glass"
              />
            </label>
            <label style={{ display: 'block', textAlign: 'left', marginBottom: 12 }}>
              <span className="form-label">New password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="input-glass"
              />
            </label>
            <label style={{ display: 'block', textAlign: 'left', marginBottom: 16 }}>
              <span className="form-label">Confirm new password</span>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="input-glass"
              />
            </label>
            <button onClick={handleUpdatePassword} disabled={savingPassword} type="button" className="btn-glass-primary btn-block">
              {savingPassword ? 'Updating…' : 'Update password'}
            </button>
          </fieldset>

          <div style={{ marginBottom: 8, opacity: 0.8 }}>
            <div><strong>UID:</strong> {user.uid}</div>
          </div>
        </div>

        <button disabled={savingProfile} type="submit" className="btn-glass-primary btn-block" style={{ marginTop: 8 }}>
          {savingProfile ? (uploadPct ? `Uploading… ${uploadPct}%` : 'Saving…') : 'Save profile'}
        </button>
      </form>
    </div>
  );
}
